import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/ui/PageHeader';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { Tag } from 'primereact/tag';
import { AlmacenService } from '../../services/AlmacenService';
import { ProductoService } from '../../services/ProductoService';
import { useAuth } from '../../context/AuthContext';
import ActionToolbar from '../../components/ui/ActionToolbar';

const Almacenes = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    const [inventory, setInventory] = useState([]);
    const [stockDialog, setStockDialog] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [globalFilter, setGlobalFilter] = useState('');
    const [loading, setLoading] = useState(false);
    const toast = useRef(null);
    const dt = useRef(null);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const resProd = await ProductoService.getAll();
            const resStock = await AlmacenService.getBySucursal(user?.idSucursal || 1);

            const productos = resProd.data || [];
            const stocks = resStock.data || [];

            const merged = [];
            productos.forEach(prod => {
                if (prod.variantes && prod.variantes.length > 0) {
                    prod.variantes.forEach(variant => {
                        const stockItem = stocks.find(s => s.idVariante === variant.id) || {
                            id: null,
                            stockTienda: 0,
                            stockAlmacen: 0,
                            stockVirtual: 0,
                            stockMinimo: 0
                        };
                        merged.push({
                            ...prod,
                            idProducto: prod.id,
                            idVariante: variant.id,
                            valorVariante: variant.valor,
                            codigoBarras: variant.codigoBarras || '-',
                            idAlmacen: stockItem.id,
                            stockTienda: stockItem.stockTienda,
                            stockAlmacen: stockItem.stockAlmacen,
                            stockVirtual: stockItem.stockVirtual,
                            stockMinimo: stockItem.stockMinimo,
                            stockTotal: stockItem.stockTienda + stockItem.stockAlmacen + (stockItem.stockVirtual || 0)
                        });
                    });
                } else {
                    merged.push({
                        ...prod,
                        idProducto: prod.id,
                        idVariante: null,
                        valorVariante: 'Sin Variantes (Crear 1)',
                        codigoBarras: '-',
                        idAlmacen: null,
                        stockTienda: 0,
                        stockAlmacen: 0,
                        stockVirtual: 0,
                        stockMinimo: 0,
                        stockTotal: 0
                    });
                }
            });

            setInventory(merged);
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar el inventario', life: 3000 });
        } finally {
            setLoading(false);
        }
    };

    const hideDialog = () => {
        setStockDialog(false);
    };

    const saveStock = async () => {
        try {
            if (!selectedProduct.idVariante) {
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'Debe crear variantes en el producto primero', life: 3000 });
                return;
            }

            const payload = {
                idVariante: selectedProduct.idVariante,
                idSucursal: user?.idSucursal || 1,
                stockAlmacen: selectedProduct.stockAlmacen,
                stockTienda: selectedProduct.stockTienda,
                stockVirtual: selectedProduct.stockVirtual || 0,
                stockMinimo: selectedProduct.stockMinimo
            };

            if (selectedProduct.idAlmacen) {
                await AlmacenService.update(selectedProduct.idAlmacen, payload);
            } else {
                await AlmacenService.create(payload);
            }

            toast.current.show({ severity: 'success', summary: 'Stock Actualizado', detail: `Inventario de ${selectedProduct.nombre} guardado`, life: 3000 });
            setStockDialog(false);
            loadData();
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo actualizar el stock', life: 3000 });
        }
    };

    const editStock = (rowData) => {
        setSelectedProduct({ ...rowData });
        setStockDialog(true);
    };

    const onInputNumberChange = (e, name) => {
        const val = e.value || 0;
        setSelectedProduct(prev => ({ ...prev, [name]: val }));
    };

    const stockTotalBodyTemplate = (rowData) => {
        const isLowStock = rowData.stockTotal <= rowData.stockMinimo;
        return (
            <Tag
                value={rowData.stockTotal}
                severity={isLowStock ? 'danger' : 'success'}
                className="text-base"
            />
        );
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <React.Fragment>
                <Button icon="pi pi-box" rounded outlined label="Ajustar Stock" onClick={() => editStock(rowData)} className="mr-2" />
            </React.Fragment>
        );
    };

    const stockDialogFooter = (
        <React.Fragment>
            <Button label="Cancelar" icon="pi pi-times" outlined onClick={hideDialog} />
            <Button label="Guardar Inventario" icon="pi pi-check" onClick={saveStock} />
        </React.Fragment>
    );

    return (
        <div className="grid crud-demo">
            <div className="col-12">
                <PageHeader 
                    title="Control de Almacén" 
                    description="Supervise el stock general de los productos en sus almacenes." 
                    icon="pi pi-building" 
                />
                <div className="card shadow-2 border-round-xl">
                    <Toast ref={toast} />

                    <ActionToolbar
                        onSearch={(val) => setGlobalFilter(val)}
                        searchValue={globalFilter}
                        searchPlaceholder="Buscar producto..."
                        extraActions={
                            <div className="flex gap-2">
                                <Button label="Ver Movimientos" icon="pi pi-list" onClick={() => navigate('/kardex')} className="border-round-md btn-primary-custom" />
                            </div>
                        }
                    />

                    <DataTable
                        ref={dt}
                        value={inventory}
                        dataKey="idVariante"
                        paginator rows={10}
                        rowsPerPageOptions={[5, 10, 25]}
                        className="datatable-responsive p-datatable-sm"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} productos"
                        globalFilter={globalFilter}
                        emptyMessage="No se encontraron productos en el inventario."
                        responsiveLayout="scroll"
                        loading={loading}
                    >
                        <Column field="codigoBarras" header="Cód. Barras" sortable headerStyle={{ minWidth: '10rem' }}></Column>
                        <Column field="nombre" header="Producto" sortable headerStyle={{ minWidth: '15rem' }}></Column>
                        <Column field="valorVariante" header="Variante" sortable headerStyle={{ minWidth: '10rem' }}></Column>
                        <Column field="stockTienda" header="Piso/Tienda" sortable headerStyle={{ minWidth: '8rem' }}></Column>
                        <Column field="stockAlmacen" header="Bodega" sortable headerStyle={{ minWidth: '8rem' }}></Column>
                        <Column field="stockVirtual" header="Virtual (E-comm)" sortable headerStyle={{ minWidth: '9rem' }}></Column>
                        <Column field="stockTotal" header="Stock Total" body={stockTotalBodyTemplate} sortable headerStyle={{ minWidth: '8rem' }}></Column>
                        <Column field="stockMinimo" header="Stock Mínimo" sortable headerStyle={{ minWidth: '8rem' }}></Column>
                        <Column body={actionBodyTemplate} headerStyle={{ minWidth: '10rem' }}></Column>
                    </DataTable>

                    <Dialog visible={stockDialog} style={{ width: '450px' }} header="Ajustar Inventario" modal className="p-fluid" footer={stockDialogFooter} onHide={hideDialog}>
                        <div className="mb-4">
                            <span className="text-xl font-bold">{selectedProduct?.nombre} - {selectedProduct?.valorVariante}</span>
                            <div className="text-500 mt-1">Código: {selectedProduct?.codigoBarras || '-'}</div>
                        </div>

                        <div className="formgrid grid">
                            <div className="field col-4">
                                <label htmlFor="stockTienda" className="font-semibold text-primary">Unidades en Tienda</label>
                                <InputNumber id="stockTienda" value={selectedProduct?.stockTienda} onValueChange={(e) => onInputNumberChange(e, 'stockTienda')} min={0} showButtons />
                            </div>
                            <div className="field col-4">
                                <label htmlFor="stockAlmacen" className="font-semibold text-primary">Unidades en Bodega</label>
                                <InputNumber id="stockAlmacen" value={selectedProduct?.stockAlmacen} onValueChange={(e) => onInputNumberChange(e, 'stockAlmacen')} min={0} showButtons />
                            </div>
                            <div className="field col-4">
                                <label htmlFor="stockVirtual" className="font-semibold text-primary">Stock Virtual (E-comm)</label>
                                <InputNumber id="stockVirtual" value={selectedProduct?.stockVirtual} onValueChange={(e) => onInputNumberChange(e, 'stockVirtual')} min={0} showButtons />
                            </div>
                        </div>

                        <div className="field mt-3">
                            <label htmlFor="stockMinimo" className="font-semibold text-orange-500">Nivel de Stock Mínimo (Alerta)</label>
                            <InputNumber id="stockMinimo" value={selectedProduct?.stockMinimo} onValueChange={(e) => onInputNumberChange(e, 'stockMinimo')} min={0} />
                            <small className="block mt-1 text-500">Si el total cae debajo de este número, el producto se resaltará en rojo.</small>
                        </div>
                    </Dialog>
                </div>
            </div>
        </div>
    );
};

export default Almacenes;
